// BUILD-046 pass F1 (Owner direction 2026-10-25, "Status filtering clarification"): ONE Status vocabulary for My Tasks, Assigned by Me, Team / Company and Shared Tasks.
//
//   All open     Active, In progress, Waiting and Blocked together; never completed, cancelled or ended work.
//   Active, In progress, Waiting, Blocked    one stored status each.
//   Completed    finished work (Team / Company reads it from its own, paged, completed list).
//   Overdue      a DATE condition over the unfinished statuses (overdueRule.ts, the one rule); it is not a stored task status.
//   Follow-up due  (BUILD-046 G4, October 23 addendum, item 2) a second DATE condition over the unfinished statuses: the next follow-up date is today or has passed (attentionModel.ts `followUpDueOn`). It reads the follow-up
//                date only, never the deadline, and is not a stored task status either. Shared Tasks does not offer it: a task nobody has taken has no person and no follow-up date to act on.
//   All statuses (Assigned by Me only) every request the requester issued, to review ended or cancelled ones.
//
// Shared Tasks holds eligible UNCLAIMED OPEN work only, so it offers just All open and Overdue: no other status applies to a task nobody has taken.
// Pure functions over the fields a row already carries; erasable TypeScript; the only import is the pure overdue rule.
import { assignmentEnded, isOpenStatus, isOverdueTask } from './overdueRule';
import { followUpDueOn } from './attentionModel';

export type StatusChoice = 'open' | 'active' | 'in_progress' | 'waiting' | 'blocked' | 'completed' | 'overdue' | 'follow-up-due';
export type IssuedStatusChoice = StatusChoice | 'all';
export type SharedStatusChoice = 'open' | 'overdue';

export interface StatusOption<T extends string = string> {
  value: T;
  label: string;
}

export const DEFAULT_STATUS = 'open' as const;

export const STATUS_OPTIONS: readonly StatusOption<StatusChoice>[] = Object.freeze([
  { value: 'open' as const, label: 'All open' },
  { value: 'active' as const, label: 'Active' },
  { value: 'in_progress' as const, label: 'In progress' },
  { value: 'waiting' as const, label: 'Waiting' },
  { value: 'blocked' as const, label: 'Blocked' },
  { value: 'completed' as const, label: 'Completed' },
  { value: 'overdue' as const, label: 'Overdue' },
  { value: 'follow-up-due' as const, label: 'Follow-up due' },
]);

/** Assigned by Me: the same eight, then every request (so an ended or cancelled one can still be reviewed). */
export const ISSUED_STATUS_OPTIONS: readonly StatusOption<IssuedStatusChoice>[] = Object.freeze([...STATUS_OPTIONS, { value: 'all' as const, label: 'All statuses' }]);

/** Shared Tasks: unclaimed open work only. */
export const SHARED_STATUS_OPTIONS: readonly StatusOption<SharedStatusChoice>[] = Object.freeze([
  { value: 'open' as const, label: 'All open' },
  { value: 'overdue' as const, label: 'Overdue' },
]);

export function isStatusChoice(value: unknown): value is StatusChoice {
  return STATUS_OPTIONS.some((option) => option.value === value);
}
export function isIssuedStatus(value: unknown): value is IssuedStatusChoice {
  return ISSUED_STATUS_OPTIONS.some((option) => option.value === value);
}
export function isSharedStatus(value: unknown): value is SharedStatusChoice {
  return SHARED_STATUS_OPTIONS.some((option) => option.value === value);
}

export function statusLabelOf(options: readonly StatusOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? options[0]?.label ?? '';
}

/** The fields of a task the open-list narrowing reads (a WorkTask of the new routes fits). */
export interface StatusTask {
  status?: unknown;
  dueDate?: unknown;
  followUpDate?: unknown;
  overdue?: unknown;
  lifecycle?: unknown;
  outcome?: unknown;
  redacted?: unknown;
}

/**
 * The rows of an OPEN list (Team / Company open work, the Shared queue) that a status choice keeps. All open keeps the list as it arrived (the server already sends only unfinished work);
 * an ended assignment is never kept by a status. Completed is not judged here: Team / Company reads finished work from its own list.
 */
export function narrowOpenList<T extends StatusTask>(tasks: readonly T[], choice: StatusChoice | SharedStatusChoice, todayIso: unknown): readonly T[] {
  if (choice === 'open') return tasks;
  if (choice === 'overdue') return tasks.filter((task) => isOverdueTask(task, todayIso));
  if (choice === 'follow-up-due') return tasks.filter((task) => typeof todayIso === 'string' && followUpDueOn(task, todayIso));
  if (choice === 'completed') return tasks.filter((task) => task.status === 'completed' && !assignmentEnded(task));
  return tasks.filter((task) => task.status === choice && !assignmentEnded(task));
}

export type OpenListCounts = Readonly<Record<'open' | 'active' | 'in_progress' | 'waiting' | 'blocked' | 'overdue' | 'follow-up-due', number>>;

/** How many rows of an open list each status choice keeps (the figures beside the choices; they describe the loaded list, which for the open lists is whole). */
export function countOpenList(tasks: readonly StatusTask[], todayIso: unknown): OpenListCounts {
  const live = tasks.filter((task) => !assignmentEnded(task));
  const of = (status: string): number => live.filter((task) => task.status === status).length;
  return {
    open: live.filter((task) => isOpenStatus(task.status)).length,
    active: of('active'),
    in_progress: of('in_progress'),
    waiting: of('waiting'),
    blocked: of('blocked'),
    overdue: live.filter((task) => isOverdueTask(task, todayIso)).length,
    'follow-up-due': live.filter((task) => typeof todayIso === 'string' && followUpDueOn(task, todayIso)).length,
  };
}
